import React, { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { invalidatePricing } from "@/lib/useLivePricing";

const EMPTY = {
    product: "foundations",
    tier_key: "",
    label: "",
    description: "",
    amount_usd: 0,
    currency: "usd",
    order: 0,
    published: true,
    is_free: false,
    is_custom_quote: false,
};

const PRODUCTS = [
    { key: "foundations", label: "HUMAN+ Foundations Experience™" },
    { key: "workshop", label: "HUMAN+ Snapshot Workshop" },
    { key: "discovery", label: "HUMAN+ Discovery Call" },
];

const PricingManager = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/pricing");
            setItems(data);
        } catch (e) {
            toast.error("Could not load pricing.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            const body = {
                ...editing,
                amount_usd: Number(editing.amount_usd),
                order: Number(editing.order) || 0,
            };
            if (editing.id) {
                await adminApi.put(`/admin/pricing/${editing.id}`, body);
            } else {
                await adminApi.post("/admin/pricing", body);
            }
            invalidatePricing();
            toast.success("Pricing saved.");
            setEditing(null);
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Save failed.";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this pricing tier?")) return;
        try {
            await adminApi.delete(`/admin/pricing/${id}`);
            invalidatePricing();
            toast.success("Deleted.");
            await load();
        } catch {
            toast.error("Delete failed.");
        }
    };

    const grouped = PRODUCTS.map((p) => ({
        ...p,
        rows: items.filter((i) => i.product === p.key),
    }));

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-montserrat font-bold text-indigo_brand text-2xl">Pricing</h2>
                    <p className="font-opensans text-sm text-[#4A5568] mt-1">
                        Update prices at any time. Changes apply on the next checkout — no redeploy needed.
                    </p>
                </div>
                <button
                    onClick={() => setEditing({ ...EMPTY })}
                    data-testid="pricing-new-btn"
                    className="ih-btn-primary"
                >
                    <Plus size={16} /> New Tier
                </button>
            </div>

            {loading ? (
                <div className="ih-card p-10 text-center text-[#4A5568]"><Loader2 className="inline animate-spin mr-2" size={16}/>Loading…</div>
            ) : (
                grouped.map((g) => (
                    <div key={g.key} className="ih-card p-6">
                        <div className="font-poppins text-[11px] font-semibold uppercase tracking-[0.2em] text-green_brand mb-3">
                            {g.label}
                        </div>
                        {g.rows.length === 0 ? (
                            <p className="font-opensans text-sm text-[#4A5568]">No tiers yet.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[#4A5568] border-b border-[#EDEFF3]">
                                            <th className="py-2 pr-4">Label</th>
                                            <th className="py-2 pr-4">Tier key</th>
                                            <th className="py-2 pr-4 text-right">Price (USD)</th>
                                            <th className="py-2 pr-4">Published</th>
                                            <th className="py-2 pr-4">Free?</th>
                                            <th className="py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {g.rows.map((r) => (
                                            <tr key={r.id} data-testid={`pricing-row-${r.tier_key}`} className="border-b border-[#F5F5F5]">
                                                <td className="py-2 pr-4 text-indigo_brand font-medium">{r.label}</td>
                                                <td className="py-2 pr-4 text-[#4A5568]">{r.tier_key}</td>
                                                <td className="py-2 pr-4 text-right tabular-nums text-indigo_brand font-semibold">
                                                    {r.is_custom_quote ? (
                                                        <span className="inline-flex items-center rounded-full bg-green_brand/15 text-green_brand px-2.5 py-1 font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]">
                                                            Custom Quote
                                                        </span>
                                                    ) : (
                                                        <>${Number(r.amount_usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                                                    )}
                                                </td>
                                                <td className="py-2 pr-4">{r.published ? "Yes" : "No"}</td>
                                                <td className="py-2 pr-4">{r.is_free ? "Yes" : "No"}</td>
                                                <td className="py-2 text-right">
                                                    <button onClick={() => setEditing(r)} className="ih-btn-ghost" data-testid={`pricing-edit-${r.tier_key}`}>
                                                        <Pencil size={14}/>Edit
                                                    </button>
                                                    <button onClick={() => del(r.id)} className="ih-btn-ghost text-red-600 ml-2">
                                                        <Trash2 size={14}/>Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ))
            )}

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing?.id ? "Edit pricing tier" : "New pricing tier"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label>Product</Label>
                                <select className="w-full mt-1 border rounded-md p-2 text-sm" value={editing.product}
                                    onChange={(e) => setEditing({ ...editing, product: e.target.value })}
                                    data-testid="pricing-product">
                                    {PRODUCTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Tier key</Label>
                                <Input value={editing.tier_key} onChange={(e) => setEditing({ ...editing, tier_key: e.target.value })} data-testid="pricing-tier-key" />
                                <p className="text-[11px] text-[#4A5568] mt-1">Short identifier, e.g. <code>regular</code>, <code>private-15</code>.</p>
                            </div>
                            <div>
                                <Label>Amount (USD)</Label>
                                <Input type="number" step="0.01" min="0" value={editing.amount_usd} onChange={(e) => setEditing({ ...editing, amount_usd: e.target.value })} data-testid="pricing-amount" />
                            </div>
                            <div className="col-span-2">
                                <Label>Display label</Label>
                                <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} data-testid="pricing-label" />
                            </div>
                            <div className="col-span-2">
                                <Label>Description</Label>
                                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="pricing-description" />
                            </div>
                            <div>
                                <Label>Order</Label>
                                <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} data-testid="pricing-published" />
                                <Label>Published</Label>
                            </div>
                            <div className="flex items-center gap-3 col-span-2">
                                <Switch checked={editing.is_free} onCheckedChange={(v) => setEditing({ ...editing, is_free: v })} />
                                <Label>Free (no checkout)</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <button onClick={() => setEditing(null)} className="ih-btn-ghost">Cancel</button>
                        <button onClick={save} disabled={saving} className="ih-btn-primary disabled:opacity-60" data-testid="pricing-save">
                            {saving ? <><Loader2 className="animate-spin" size={14}/> Saving…</> : "Save"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PricingManager;
